import { useState, useCallback, useMemo } from 'react';

interface SortBy {
  field: string;
  asc: boolean;
}

interface PaginationAndSortOptions {
  initialPageSize?: number;
  initialSortBy?: SortBy | null;
  pageSizeOptions?: number[];
  initialPage?: number;
}

interface PaginationPayload {
  current: number;
  pageSize: number;
}

interface SorterItem {
  field?: string | number | readonly (string | number)[];
  order?: 'ascend' | 'descend';
  columnKey?: string;
}

interface TableOnChangeExtra {
  currentDataSource: readonly any[];
  action: 'paginate' | 'sort' | 'filter';
}

export function usePaginationAndSort({
  initialPageSize = 60,
  initialSortBy = null,
  pageSizeOptions = [20, 60, 100],
  initialPage = 1,
}: PaginationAndSortOptions = {}) {
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [totalItems, setTotalItems] = useState(0);
  const [sortBy, setSortBy] = useState<SortBy | null>(initialSortBy);

  const handlePageChange = useCallback(
    (page: number, size?: number) => {
      setCurrentPage(page);
      if (size && size !== pageSize) {
        setPageSize(size);
        setCurrentPage(1);
      }
    },
    [pageSize],
  );

  const handlePageSizeChange = useCallback(
    (value) => {
      const newSize = typeof value === 'object' ? value.value : value;
      if (newSize && newSize !== pageSize) {
        setPageSize(newSize);
        setCurrentPage(1);
      }
    },
    [pageSize],
  );

  // Antd table onChange 处理函数签名:
  // onChange(pagination, filters, sorter, extra)
  const handleTableChange = useCallback(
    (
      pagination: PaginationPayload,
      filters: Record<string, any>,
      sorter: SorterItem | SorterItem[],
      extra: TableOnChangeExtra,
    ) => {
      if (extra.action === 'sort') {
        const currentSorter = Array.isArray(sorter) ? sorter[0] : sorter;
        // 如果field未定义，则使用columnKey
        const sortField = currentSorter?.field ?? currentSorter?.columnKey;
        if (currentSorter && sortField && currentSorter.order) {
          const asc = currentSorter.order === 'ascend';
          const field = String(sortField);
          console.log(`Setting sortBy: { field: ${field}, asc: ${asc} }`); // 记录最终的sortBy值
          setSortBy({ field: field, asc });
        } else {
          setSortBy(null);
        }
        if (currentPage !== 1) {
          setCurrentPage(1);
        }
      }
      // 可选择也处理表格更改事件中的分页
      // else if (extra.action === 'paginate') {
      //   handlePageChange(pagination.current, pagination.pageSize);
      // }
    },
    [currentPage],
  );

  const firstRow = useMemo(
    () => (currentPage - 1) * pageSize,
    [currentPage, pageSize],
  );

  return {
    // State
    currentPage,
    pageSize,
    sortBy,
    totalItems,
    firstRow,
    pageSizeOptions,

    // Setters
    // setCurrentPage,
    // setPageSize,
    // setSortBy,
    setTotalItems,

    // Handlers
    handlePageChange,
    handlePageSizeChange,
    handleTableChange,
  };
}
